import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import classnames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import OutdoorsyChart from '@/assets/about/outdoorsy-group/outdoorsy-chart.svg';
import OutdoorsyTicker from '@/assets/about/outdoorsy-group/outdoorsy-ticker.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { useBreakpoint } from '@/hooks/useBreakpoint';

interface IByTheNumbersItem {
  leftItemText: string;
  rightItemText: string;
  hasDollarSign?: boolean;
}

const ByTheNumbers = () => {
  const intl = useIntl();
  const { isAboveTablet, isAboveDesktop } = useBreakpoint();

  let sectionHeading = EHeadingStyleVariant.H2Alt;

  if (isAboveTablet) {
    sectionHeading = EHeadingStyleVariant.H4Alt;
  }

  if (isAboveDesktop) {
    sectionHeading = EHeadingStyleVariant.H3Alt;
  }

  const data = [
    {
      leftItemText: intl.formatMessage({ defaultMessage: '+$1b', id: 'i8Utc3' }),
      rightItemText: intl.formatMessage({ defaultMessage: 'Host revenue', id: 'NxdQhd' }),
    },
    {
      leftItemText: intl.formatMessage({ defaultMessage: '+8 million', id: '+blfSm' }),
      rightItemText: intl.formatMessage({ defaultMessage: 'Nights outside', id: 'bnmtOE' }),
    },
    {
      leftItemText: intl.formatMessage({ defaultMessage: '137%', id: '5qYtA+' }),
      rightItemText: intl.formatMessage({ defaultMessage: 'Long term CAGR', id: 'yFLviF' }),
    },
  ];

  return (
    <section className="py-20 md:py-24">
      <MaxWidth className="justify-center px-6 mx-auto">
        <Heading className="pb-6 md:pb-20" variant={sectionHeading}>
          <FormattedMessage defaultMessage="By the numbers" id="NGw5Tv" />
        </Heading>

        <div className="flex flex-col md:flex-row">
          <OutdoorsyChart className="w-auto h-auto" />
          <OutdoorsyTicker className="w-auto h-auto mt-6 md:mt-0" />
        </div>

        <div className="flex flex-col pt-12">
          {data.map((item, index) => (
            <ByTheNumbersItem
              key={index}
              leftItemText={item.leftItemText}
              rightItemText={item.rightItemText}
            />
          ))}
        </div>
      </MaxWidth>
    </section>
  );
};

const ByTheNumbersItem: React.FC<IByTheNumbersItem> = ({
  leftItemText,
  rightItemText,
  hasDollarSign,
}) => {
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <div className="flex items-center py-4 border-t border-[#DBDAD2]">
        <Heading variant={EHeadingStyleVariant.H4}>
          {hasDollarSign && <span>$</span>} {leftItemText}
        </Heading>
        <Text className="flex-1 text-end" variant={ETextStyleVariant.SmallRegular}>
          {rightItemText}
        </Text>
      </div>
    );
  }

  return (
    <div
      className={classnames('flex items-center py-4', {
        'border-t  border-[#DBDAD2]': !hasDollarSign,
      })}>
      <div className="flex">
        {hasDollarSign && <div className="mr-2 text-900">$</div>}
        <Heading className="xxxl:text-[96px]" variant={EHeadingStyleVariant.H1}>
          {leftItemText}
        </Heading>
      </div>
      <Heading className="flex-1 text-end" variant={EHeadingStyleVariant.H5}>
        {rightItemText}
      </Heading>
    </div>
  );
};

export default ByTheNumbers;
